/**
 * Shared library for HustlePlaza shop components.
 * - Axios instance + session id
 * - Money formatter
 * - Cart persistence helpers
 * - Storefront API wrappers (sm namespace)
 */
import axios from "axios";

export const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export const SESSION_KEY = "hpz-session-id";
export const CART_KEY = "hpz-cart-v1";

export const fmtMoney = (cents) =>
  ((cents || 0) / 100).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export function getSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = "s_" + Math.random().toString(36).slice(2, 10) +
        Math.random().toString(36).slice(2, 6);
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

export function loadCart() {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY)) || [];
  } catch { return []; }
}

export function saveCart(lines) {
  localStorage.setItem(CART_KEY, JSON.stringify(lines));
}

export const api = axios.create({ baseURL: API, withCredentials: true });
export const sid = getSessionId();

export const sm = {
  listProducts: (q) =>
    api.get(`/api/dropshipzone/products`, { params: q }).then(r => r.data),
  storefrontSubcategories: (name) =>
    api.get(`/api/dropshipzone/storefronts/${encodeURIComponent(name)}/subcategories`)
       .then(r => r.data).catch(() => []),
  storefrontSummary: (name) =>
    api.get(`/api/plaza/storefronts/${encodeURIComponent(name)}/summary`)
       .then(r => r.data)
       .catch(() => ({ live: 0, total: 0, subcategories: [] })),
  getProduct: (sku) =>
    api.get(`/api/dropshipzone/products/${encodeURIComponent(sku)}`).then(r => r.data),
  listCategories: () =>
    api.get(`/api/dropshipzone/categories`).then(r => r.data),
  trackView: (sku) =>
    api.post(`/api/plaza/smart/track/view`, { sku, session_id: sid }).catch(() => {}),
  trackCart: (sku, action, qty = 1, email = null) =>
    api.post(`/api/plaza/smart/track/cart`,
        { sku, action, qty, session_id: sid, email }).catch(() => {}),
  recentlyViewed: () =>
    api.get(`/api/plaza/smart/recently-viewed`,
        { params: { session_id: sid, limit: 12 } }).then(r => r.data),
  recommendations: (sku) =>
    api.get(`/api/plaza/smart/recommendations`,
        { params: { sku, limit: 8 } }).then(r => r.data),
  notifyRestock: (sku, email) =>
    api.post(`/api/plaza/smart/notify-restock`,
        { sku, email, session_id: sid }).then(r => r.data),
  wishlistToggle: (sku) =>
    api.post(`/api/plaza/smart/wishlist/toggle`,
        { sku, session_id: sid }).then(r => r.data),
  wishlist: () =>
    api.get(`/api/plaza/smart/wishlist`,
        { params: { session_id: sid } }).then(r => r.data),
  freeShipping: () =>
    api.get(`/api/plaza/smart/free-shipping`).then(r => r.data),
  wallet: () =>
    api.get(`/api/plaza/smart/wallet`).then(r => r.data),
  reviews: (sku) =>
    api.get(`/api/plaza/smart/reviews/${encodeURIComponent(sku)}`).then(r => r.data),
  submitReview: (payload) =>
    api.post(`/api/plaza/smart/reviews`, { ...payload, session_id: sid }).then(r => r.data),
  fuzzySearch: (q, category_id, storefront) =>
    api.get(`/api/plaza/pricing/search`,
        { params: { q, limit: 60, session_id: sid,
                    ...(category_id ? { category_id } : {}),
                    ...(storefront ? { storefront } : {}) } })
       .then(r => r.data),
  catalogCount: () =>
    api.get(`/api/plaza/pricing/count`)
       .then(r => r.data)
       .catch(() => ({ live: 0, total: 0 })),
  suggest: (q, limit = 6) => {
    if (!q || q.trim().length < 2) return Promise.resolve({ results: [] });
    return api.get(`/api/plaza/pricing/suggest`,
        { params: { q, limit } })
       .then(r => r.data)
       .catch(() => ({ results: [] }));
  },
  socialProof: (skus, days = 7) => {
    if (!skus || !skus.length) return Promise.resolve({ counts: {} });
    return api.get(`/api/plaza/pricing/social-proof`,
        { params: { skus: skus.join(","), days } })
       .then(r => r.data)
       .catch(() => ({ counts: {} }));
  },
  compare: (sku) =>
    api.get(`/api/plaza/pricing/compare/${encodeURIComponent(sku)}`).then(r => r.data),
  topSearched: () =>
    api.get(`/api/plaza/discover/top-searched`).then(r => r.data),
  topPurchased: () =>
    api.get(`/api/plaza/discover/top-purchased`).then(r => r.data),
  trendingCategories: () =>
    api.get(`/api/plaza/discover/trending-categories`).then(r => r.data),
  reviewPrompts: (sku) =>
    api.get(`/api/plaza/smart/reviews/${encodeURIComponent(sku)}/prompts`)
       .then(r => r.data).catch(() => ({ prompts: [] })),
  signal: (payload) =>
    api.post(`/api/signals/log`, { sid, platform: "plaza", ...payload })
       .catch(() => null),
  amazonLookup: (q) =>
    api.post(`/api/plaza/amazon/lookup`, { q, sid })
       .then(r => r.data).catch(() => ({ found: false })),
  activePromo: () =>
    api.get(`/api/plaza/promos/active`).then(r => r.data)
       .catch(() => ({ active: false })),
};
